import type { FlowStep, PathSpec } from './flow-steps.tsx';
import type { FlowGraph } from './flow-graph.tsx';

interface BuildGraphParams<State> {
  basePath: `/${string}`;
  graph: FlowGraph;
  spec: PathSpec;
  steps: Record<string, FlowStep<State>>;
  state: State;
  prev?: string;
}

export function buildGraph<State>({
  basePath,
  graph,
  spec,
  steps,
  state,
  prev,
}: BuildGraphParams<State>): void {
  function find(id: string): FlowStep<State> {
    const node = steps[id];
    if (node === undefined) {
      throw new Error(`Flow node '${id}' not found`);
    }
    return node;
  }

  const step = find(spec.id);
  const path = basePath + step.path(spec.params ?? {});

  const ensured = graph.ensure(path, spec);

  if (prev !== undefined) {
    graph.linkNext(prev, path);
  }

  if (!ensured) {
    return;
  }

  const discovered = graph.discovered();
  if (prev !== undefined && step.checkpoint?.({ state, prev: spec, discovered }) !== true) {
    graph.linkPrev(prev, path);
  }

  const next = step.next?.({ state, prev: spec, discovered }) ?? null;
  if (next === null) {
    return;
  }

  buildGraph({ graph, spec: next, steps, state, prev: path, basePath });

  next.branches?.forEach(newSpec => {
    buildGraph({ graph, spec: newSpec, steps, state, prev: path, basePath });
  });
}
